import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import { Globals } from "@coreapp/CoreApp/Globals";
import { isResourceRoute, legacyToRouteParamMap } from "@coreapp/constants";

/**
 * One-time redirect from legacy query-param URLs to new resource routes.
 *
 * Plain legacy URLs:   ?chartID=123&mode=chart  →  /base/123?mode=chart
 * Secure deep links:   ?dl=<base64 blob>        →  /base/16?mode=chart&view=Headcount&...
 *
 * For ?dl= URLs, LoadURLParameters decodes the blob and sets globals as
 * normal. It also maps the decoded legacy params to new names via
 * legacyToRouteParamMap and stores them on fDecodedDLParams.
 * After the coreapp loads the chart and updateUrlToResourceRoute sets the
 * path to /base/{id}, this hook reads the map and appends all query params.
 *
 * Uses `replace: true` so the legacy URL doesn't appear in browser history.
 */
export const useLegacyUrlRedirect = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasRedirected = useRef(false);

  // --- Plain legacy URLs: redirect immediately ---
  useEffect(() => {
    if (hasRedirected.current) return;

    const params = new URLSearchParams(location.search);
    if (params.has("dl")) return;

    const isOnGenericPath =
      location.pathname === "/" ||
      location.pathname === "/chart" ||
      location.pathname === "/edit" ||
      location.pathname === "/directory" ||
      location.pathname === "/planning";

    if (!isOnGenericPath) return;

    const resolved = resolveLaunchParams(params, location.pathname);
    if (!resolved) return;

    const { path, query } = resolved;
    hasRedirected.current = true;

    const search = query.toString();
    const target = search ? `${path}?${search}` : path;
    navigate(target, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // dl= URLs: append params after updateUrlToResourceRoute sets the path
  useEffect(() => {
    if (hasRedirected.current) return;

    const dlParams = Globals._accountInfo?.fDecodedDLParams as
      Record<string, string> | null | undefined;
    if (!dlParams) return;
    if (!isResourceRoute(location.pathname)) return;

    hasRedirected.current = true;
    Globals._accountInfo.fDecodedDLParams = null;

    // Path-building keys are already consumed by updateUrlToResourceRoute —
    // only query-level params get appended to the URL.
    const params = new URLSearchParams(location.search);
    params.delete("dl");

    for (const [key, value] of Object.entries(dlParams)) {
      if (PATH_KEYS.has(key)) continue;
      if (key === "mode" && value.toLowerCase() === "directory") {
        params.set("display", "directory");
      } else {
        const normalized = key === "mode" ? normalizeLegacyMode(value) : value;
        if (normalized) params.set(key, normalized);
      }
    }

    const search = params.toString();
    navigate(search ? `${location.pathname}?${search}` : location.pathname, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Keys that become path segments — excluded from query params. */
const PATH_KEYS = new Set(["chartID", "file", "directory", "planID"]);

/**
 * Build the resource route path and query params from plain legacy URL params.
 * Uses legacyToRouteParamMap so adding a new param is a one-line change.
 * Returns null when no navigable params are present.
 */
function resolveLaunchParams(
  urlParams: URLSearchParams,
  pathname: string
): { path: string; query: URLSearchParams } | null {
  const chartID = urlParams.get("chartID");
  const file = urlParams.get("file");
  const directory = urlParams.get("directory");
  const planID = urlParams.get("launchPlanID");

  let path: string | null = null;
  if (chartID) {
    path = `/base/${encodeURIComponent(chartID)}`;
  } else if (file && directory) {
    path = `/open/${encodeURIComponent(directory)}/${encodeURIComponent(file)}`;
  } else if (planID) {
    path = `/plan/${encodeURIComponent(planID)}`;
  }

  if (!path && pathname === "/directory") {
    path = "/";
  }
  if (!path) return null;

  const query = new URLSearchParams();

  for (const [legacyKey, newKey] of Object.entries(legacyToRouteParamMap) as [string, string][]) {
    if (PATH_KEYS.has(newKey)) continue;
    const val = urlParams.get(legacyKey);
    if (val == null) continue;

    if (newKey === "mode") {
      const normalized = normalizeLegacyMode(val);
      if (normalized === "directory") {
        query.set("display", "directory");
      } else if (normalized) {
        query.set("mode", normalized);
      }
    } else {
      query.set(newKey, val);
    }
  }

  if (pathname === "/directory" && !query.has("display")) {
    query.set("display", "directory");
  }

  return { path, query };
}

/** Normalize legacy mode param values to the new mode vocabulary. */
function normalizeLegacyMode(mode: string): string | null {
  switch (mode.toLowerCase()) {
    case "chart":
    case "viewer":
    case "portal":
    case "iframe":
      return "chart";
    case "edit":
    case "workbench":
    case "standard":
      return "edit";
    case "directory":
      return "directory";
    case "planning":
      return "planning";
    case "background":
      return "background";
    case "reports":
      return "reports";
    case "viewmanager":
      return "edit";
    default:
      return null;
  }
}
